
import Vue from 'vue';
import _sortBy from 'lodash/sortBy';
import _isEmpty from 'lodash/isEmpty';
import { analytics, EVENTS } from '@/services/analytics';
import { mapActions, mapState } from 'vuex';
import {
	FEATURED_RUNNIT_NODE_DEF_IDS,
	RUNNITS_OWNER_SELECTION,
} from '@/views/Runnits/constants';
import {
	RUNNITS_PUBLISHED_STATE,
	RunnitsPublishedState,
} from '@/constants/constants';
import {
	RunnitNodeDef,
	UserRunnitNodeDefInfo,
	RUNNIT_NODE_DEF_TAG_TYPE,
	RUNNIT_NODE_DEF_TAG_TYPE_SORT,
	RUNNIT_NODE_DEF_TOOL_TYPE,
	RUNNIT_TYPE,
	RUNNIT_NODE_DEF_TOOL_APP_TYPE,
	RunnitNodeDefTagType,
} from '@run-diffusion/shared';
import OfferingCardWithLabels from '@/components/OfferingCardWithLabels.vue';
import GlassButton from '@/components/base/GlassButton.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import EmptyStateCircleBackground from '@/components/states/EmptyStateCircleBackground.vue';
import { RunnitsCRUDMixin, RunnitsAndRunnitNodeDefsBindingsMixin, TeamUserMixin } from '@/mixins';
import RunnitTagTypeTabs from '../RunnitTagTypeTabs.vue';
import RunnitDuplicateEditNodeDefInternalEditor from '../RunnitSettings/internalAdminOnly/RunnitDuplicateEditNodeDefInternalEditor.vue';
import RunnitTagFilterCarousel from '../RunnitTagFilterCarousel.vue';
import { APP_BAR_HEIGHT } from '@/components/Toolbar/constants';
import RunnitIntentionsBar from '../RunnitIntentionsBar.vue';
import { fuzzyFilter } from '@/utils/fuzzySearch';
import RunnitToolPromoCard from '../RunnitToolPromoCard.vue';

export default Vue.extend({
	name: 'RunnitAllToolsPage',
	metaInfo: {
		title: 'Tools',
	},
	mixins: [
		RunnitsCRUDMixin,
		RunnitsAndRunnitNodeDefsBindingsMixin,
		TeamUserMixin,
	],
	data () {
		return {
			APP_BAR_HEIGHT,
			RUNNITS_PUBLISHED_STATE,
			RUNNIT_NODE_DEF_TOOL_TYPE,
			FEATURED_RUNNIT_NODE_DEF_IDS,
			toolSearchValue: '',
			selectedCategory: 'ALL',
			filteredRunnitNodeDefs: [],
			nodeDefInternalEditorDialog: {
				open: false,
				selectedNodeDef: null,
			},
			publishedStateFilter: RUNNITS_PUBLISHED_STATE.PUBLISHED,
			selectedTagsFilterMap: {},
			nodeDefsByTagMap: {},
		};
	},
	mounted () {
		const initialTagId = this.$route.params.tag;
		const searchQuery = this.$route.query.search;

		if (searchQuery) {
			this.toolSearchValue = searchQuery;
			this.applyFilters();
		}

		if (initialTagId) {
			const initialTag = this.tagsById[this.$route.params.tag];
			if (initialTag) {
				const initializeFiltersAfterRunnitsLoaded = () => {
					if (this.loadingLimitNumRunnits === false) {
						this.handleCategoryChange(initialTag.type);
						this.toggleTagFilter(initialTag);
					} else {
						setTimeout(() => {
							initializeFiltersAfterRunnitsLoaded()
						}, 100);
					}
				}
				initializeFiltersAfterRunnitsLoaded();
			}
		}
	},
	computed: {
		...mapState([
			'user',
			'loadingLimitNumRunnits',
			'loadingRecentUserRunnitNodeDefInfos',
			'recentUserRunnitNodeDefInfos',
			'runnitState',
			'trainerNodeDefs',
			'loadingTrainerNodeDefs',
			'publicRunnitNodeDefs',
			'loadingPublicRunnitNodeDefs',
			'companyRunnitNodeDefs',
			'loadingCompanyRunnitNodeDefs',
			'publicRunnitNodeDefTagsMap',
		]),
		visibleRunnitNodeDefInfos () {
			return this.recentUserRunnitNodeDefInfos.filter((toolInfo: UserRunnitNodeDefInfo) => (
				!toolInfo.nodeDef.isDeleted &&
				(
					this.user &&
					toolInfo.nodeDef.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.RUNNIT &&
					(
						this.user.isAdmin ||
						toolInfo.nodeDef.isPublished
					)
				) &&
				(
					toolInfo.nodeDef.type !== RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY ||
					(
						this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
						this.team &&
						toolInfo.nodeDef.companyIds?.[this.team.companyId]
					)
				) &&
				!this.isNodeDefBlockedForTeamUser(toolInfo.nodeDef)
			)).slice(0, 5);
		},
		visibleRunnitNodeDefs () {
			return _sortBy(this.filteredRunnitNodeDefs.filter((nodeDef: RunnitNodeDef) => (
				this.filteredTagTypes.some(tag => _isEmpty(nodeDef.tags) || nodeDef.tags[tag])
			)), 'title');
		},
		tagsOnSelectedType () {
			const excludedTypes: RunnitNodeDefTagType[] = [
				RUNNIT_NODE_DEF_TAG_TYPE.HOME_PAGE,
				RUNNIT_NODE_DEF_TAG_TYPE.INDUSTRY,
				RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_TYPE,
				RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_QUALITY,
				RUNNIT_NODE_DEF_TAG_TYPE.INTERNAL,
			];
			return this.sortedTagType.map((tagId) => (this.tagsById[tagId])).filter(t => t && (this.user.isAdmin || !excludedTypes.includes(t.type)));
		},
		noFilterTagsSelected () {
			return !Object.keys(this.selectedTagsFilterMap).length ||
				Object.keys(this.selectedTagsFilterMap).every(tagId => !this.selectedTagsFilterMap[tagId]);
		},
		filteredTagTypes () {
			return this.sortedTagType.filter(tagId => this.noFilterTagsSelected ||
				this.selectedTagsFilterMap[tagId]);
		},
		sortedTagType () {
			return _sortBy(Object.keys(this.nodeDefsByTagMap), [
				(tagId) => { return RUNNIT_NODE_DEF_TAG_TYPE_SORT[this._get(this.tagsById, `[${tagId}].type`)] },
				(tagId) => { return this._get(this.tagsById, `[${tagId}].sortOrder`) },
				(tagId) => { return this._get(this.tagsById, `[${tagId}].label`) },
			])
				.filter(tagId =>
					this.selectedCategory === 'ALL' ||
					this.selectedCategory === 'COMPANY' ||
					this._get(this.tagsById, `[${tagId}].type`) === this.selectedCategory ||
					tagId === 'uncategorized'
				);
		},
		tagsById () {
			return {
				'uncategorized': { label: 'Uncategorized', id: 'uncategorized', type: 'uncategorized' },
				...this.publicRunnitNodeDefTagsMap,
			}
		},
	},
	watch: {
		filteredRunnitNodeDefs: {
			immediate: true,
			handler (newVal: RunnitNodeDef[]) {
				this.mapNodeDefsToTag(newVal);
			}
		},
		loadingPublicRunnitNodeDefs: {
			immediate: true,
			handler (newVal) {
				if (newVal === false && !this.loadingCompanyRunnitNodeDefs && !this.loadingTrainerNodeDefs) {
					this.applyFilters();
				}
			}
		},
		loadingCompanyRunnitNodeDefs: {
			immediate: true,
			handler (newVal) {
				if (newVal === false && !this.loadingPublicRunnitNodeDefs && !this.loadingTrainerNodeDefs) {
					this.applyFilters();
				}
			}
		},
		loadingTrainerNodeDefs: {
			immediate: true,
			handler (newVal) {
				if (newVal === false && !this.loadingPublicRunnitNodeDefs && !this.loadingCompanyRunnitNodeDefs) {
					this.applyFilters();
				}
			}
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		handleCategoryChange (selectedCategory: RunnitNodeDefTagType) {
			this.selectedCategory = selectedCategory;
			this.selectedTagsFilterMap = {};
			this.applyFilters();
		},
		handleSearchInput (searchValue) {
			this.$router.replace({ query: { ...this.$route.query, search: searchValue } });
			this.toolSearchValue = searchValue;
			this.applyFilters();
		},
		handlePublishedFilterChange (publishedState: RunnitsPublishedState) {
			this.publishedStateFilter = publishedState;
			this.applyFilters();
		},
		applyFilters () {
			this.filteredRunnitNodeDefs = [];
			[
				...this.publicRunnitNodeDefs,
				...this.companyRunnitNodeDefs,
				...(this.user.isAdmin ? this.trainerNodeDefs : []),
			].forEach((runnitNodeDef: RunnitNodeDef) => {
				let matchesSearch: boolean, matchesPublishedState: boolean, matchesSelectedTypes: boolean, matchesCompany: boolean, matchesNotBlocked: boolean;

				if (!this.toolSearchValue) {
					matchesSearch = true;
				} else {
					matchesSearch = fuzzyFilter(
						[runnitNodeDef],
						this.toolSearchValue,
						[
							{ name: 'title', weight: 0.7 },
							{ name: 'description', weight: 0.3 }
						]
					).length > 0;
				}

				matchesPublishedState = !!(
					(this.publishedStateFilter === RUNNITS_PUBLISHED_STATE.PUBLISHED && runnitNodeDef.publishedAt) ||
					(this.publishedStateFilter === RUNNITS_PUBLISHED_STATE.NOT_PUBLISHED && !runnitNodeDef.publishedAt)
				);

				if (this.selectedCategory === 'COMPANY') {
					matchesSelectedTypes = runnitNodeDef.type === RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY;
				} else if (this.selectedCategory && this.selectedCategory !== 'ALL') {
					matchesSelectedTypes = !!(
						_isEmpty(runnitNodeDef.tagTypes) ||
						runnitNodeDef.tagTypes[this.selectedCategory] ||
						Object.values(runnitNodeDef.tagTypes).every(applied => !applied)
					);
				} else {
					matchesSelectedTypes = true;
				}

				matchesCompany = !!(
					runnitNodeDef.type !== RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY ||
					(
						this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
						this.team?.companyId &&
						runnitNodeDef.companyIds?.[this.team.companyId]
					)
				);

				matchesNotBlocked = !this.isNodeDefBlockedForTeamUser(runnitNodeDef);

				if (matchesSearch && matchesPublishedState && matchesSelectedTypes && matchesCompany && matchesNotBlocked) {
					this.filteredRunnitNodeDefs.push(runnitNodeDef);
				}
			});

			this.filteredRunnitNodeDefs = _sortBy(this.filteredRunnitNodeDefs, 'sortOrder');
		},
		async onToolCardClick (runnitNodeDef: RunnitNodeDef) {
			analytics.track(EVENTS.TOOL_CARD_CLICKED, {
				tool_id: this._get(runnitNodeDef, 'id', null),
				tool_title: this._get(runnitNodeDef, 'title', null),
				tool_type: this._get(runnitNodeDef, 'type', null),
				source: 'all_tools_page',
			});
			await this.onAddRunnitClick(runnitNodeDef, RUNNIT_TYPE.SINGLE_TOOL);
		},
		toggleAdminNodeDefEditor (selectedNodeDef: RunnitNodeDef) {
			this.nodeDefInternalEditorDialog = {
				...this.nodeDefInternalEditorDialog,
				open: true,
				selectedNodeDef,
			};
		},
		onEditDialogClose (open: boolean) {
			this.nodeDefInternalEditorDialog = {
				...this.nodeDefInternalEditorDialog,
				open: !!open,
			};
			this.applyFilters();
		},
		toggleTagFilter (tag) {
			this.selectedTagsFilterMap = {
				...this.selectedTagsFilterMap,
				[tag.id]: !this.selectedTagsFilterMap[tag.id],
			};
		},
		async mapNodeDefsToTag (nodeDefs) {
			this.nodeDefsByTagMap = (nodeDefs || []).reduce((map, nodeDef) => {
				Object.keys(nodeDef.tags || []).map((tagId) => {
					if (!map[tagId]) {
						map[tagId] = [];
					}
					map[tagId].push(nodeDef);
				})
				if (!nodeDef.tags || _isEmpty(nodeDef.tags)) {
					if (!map['uncategorized']) {
						map['uncategorized'] = [];
					}
					map['uncategorized'].push(nodeDef);
				}
				return map;
			}, {});
		},
	},
	components: {
		GlassButton,
		LoadingState,
		OfferingCardWithLabels,
		EmptyStateCircleBackground,
		RunnitTagTypeTabs,
		RunnitDuplicateEditNodeDefInternalEditor,
		RunnitTagFilterCarousel,
		RunnitIntentionsBar,
		RunnitToolPromoCard,
	},
});
